import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of, Subject } from 'rxjs';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { CodeableConceptField, CodeField, GroupField, IndividualField, IndividualReferenceField, ReferenceFieldArray, formMetaData, generalFieldsData, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ErrorService } from '../shared/error.service';
import { Bundle } from 'fhir/r4';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../shared/notification/notification.service';
import { backendEndPointToken } from '../app.config';
import { MatSnackBar } from '@angular/material/snack-bar';

type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;

@Injectable({
  providedIn: 'root'
})
export class HospitalService {
  dialog = inject(MatDialog);
  http = inject(HttpClient);
  notif = inject(NotificationService);
  snackBar = inject(MatSnackBar);
  formFieldsDataService = inject(FormFieldsSelectDataService);
  errorService = inject(ErrorService);

  private backendUrl = inject(backendEndPointToken);

  // Subject to emit organization updates
  private organizationUpdated$ = new Subject<any>();
  
  // Observable for components to subscribe to
  public onOrganizationUpdated = this.organizationUpdated$.asObservable();

  emitOrganizationUpdate(updated: any): void {
    this.organizationUpdated$.next(updated);
  }

  /**
   * Edit existing organization - prepopulates form with current data
   */
  editOrganization(organizationData: any) {
    forkJoin({
      sample: of('sample')
    }).subscribe((e: any) => {
      const prepopulatedData = this.transformOrganizationToFormData(organizationData);
      
      const dialogRef = this.dialog.open(DynamicFormsV2Component, {
        maxHeight: '90vh',
        maxWidth: '900px',
        autoFocus: false,
        data: {
          formMetaData: {
            formName: "Edit Hospital Record",
            formDescription: "Edit Hospital Information",
            submitText: "Update",
            resourceId: organizationData.id,
            resourceType: 'Organization',
            closeDialogOnSubmit: true
          },
          prepopulatedData: prepopulatedData,
          formFields: this.getOrganizationFormFields()
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && !result.cancelled && result.values) {
          console.log('📤 Submitting updated organization:', result.values);
          this.updateOrganization(organizationData.id, result.values, organizationData);
        } else {
          console.log('❌ Edit cancelled or no data');
        }
      });
    });
  }

  /**
   * Transform form data back to FHIR Organization format and submit to backend
   * @param orgId - Organization ID
   * @param formData - Updated form data
   * @param originalOrg - Original organization data (to preserve extensions)
   */
  private updateOrganization(orgId: string, formData: any, originalOrg?: any) {
    const updatedOrg = this.transformFormDataToOrganization(formData, orgId, originalOrg);
    
    console.log('🔄 Sending PUT request to update organization:', updatedOrg);
    
    this.http.put(`${this.backendUrl}/Organization/${orgId}`, updatedOrg, {
      headers: { 'Content-Type': 'application/fhir+json' }
    }).subscribe({
      next: (response) => {
        console.log('✅ Organization updated successfully:', response);
        
        this.snackBar.open('✅ Organization updated successfully', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });

        // Fetch fresh data from backend and emit update
        this.refreshOrganizationData(orgId);
      },
      error: (err) => {
        console.error('❌ Failed to update organization:', err);
        
        this.snackBar.open('❌ Failed to update organization', 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
        
        this.errorService.openandCloseError('Failed to update organization');
      }
    });
  }

  /**
   * Fetch fresh organization data from backend and emit to subscribers
   * @param orgId - Organization ID to refresh
   */
  private refreshOrganizationData(orgId: string): void {
    console.log('🔄 Refreshing organization data:', orgId);

    this.http.get(`${this.backendUrl}/Organization/${orgId}`, {
      headers: { 'Accept': 'application/fhir+json' }
    }).subscribe({
      next: (freshData) => {
        console.log('✅ Fresh organization data received:', freshData);
        
        // Emit the fresh data to all subscribers
        this.organizationUpdated$.next(freshData);
      },
      error: (err) => {
        console.error('❌ Failed to refresh organization data:', err);
        
        this.snackBar.open('⚠️ Updated successfully but failed to refresh display', 'Close', {
          duration: 4000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  /**
   * Transform FHIR Organization format to form data
   */
  private transformFormDataToOrganization(formData: any, orgId: string, originalOrg?: any): any {
    const org: any = {
      resourceType: 'Organization',
      id: orgId,
      name: formData.name || undefined
    };

    // Preserve active status from original
    if (originalOrg?.active !== undefined) {
      org.active = originalOrg.active;
    }

    // Preserve meta from original (versionId, lastUpdated)
    if (originalOrg?.meta) {
      org.meta = originalOrg.meta;
    }

    // TYPE
    if (formData.type) {
      org.type = [{
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/organization-type',
          code: this.mapOrgTypeToCode(formData.type),
          display: formData.type
        }],
        text: formData.type
      }];
    }

    // EXTENSIONS: Selective partial update strategy
    let extensions: any[] = [];

    // Step 1: Preserve ALL non-editable extensions from original
    if (originalOrg?.extension && Array.isArray(originalOrg.extension)) {
      originalOrg.extension.forEach((ext: any) => {
        const url = ext.url || '';
        
        const isProtected = 
          url.includes('is-parent-organization') ||
          url.includes('parent') ||
          url.includes('parentOrganization') ||
          url.includes('parentOrganizationName');
        
        const isEditable = 
          url.includes('organization-description') ||
          url.includes('organization-qualification');
        
        if (isProtected || !isEditable) {
          extensions.push(JSON.parse(JSON.stringify(ext)));
        }
      });
    }

    // Step 2: Update or add description extension
    if (formData.description !== undefined) {
      const descIndex = extensions.findIndex(ext => 
        ext.url === 'http://elikita.org/fhir/StructureDefinition/organization-description'
      );
      
      if (descIndex >= 0) {
        extensions[descIndex].valueString = formData.description;
      } else {
        extensions.push({
          url: 'http://elikita.org/fhir/StructureDefinition/organization-description',
          valueString: formData.description
        });
      }
    }

    // Step 3: Update qualifications from form
    if (formData.qualification && Array.isArray(formData.qualification)) {
      extensions = extensions.filter(ext => 
        !ext.url?.includes('organization-qualification')
      );

      formData.qualification.forEach((qual: any) => {
        const qualExtension: any = {
          url: 'http://localhost:5289/fhir/organization-qualification',
          extension: []
        };

        if (qual.code) {
          qualExtension.extension.push({
            url: 'description',
            valueString: qual.code
          });
        }

        if (qual.issuer_name) {
          qualExtension.extension.push({
            url: 'issuer',
            valueString: qual.issuer_name
          });
        }

        if (qual.issuer_website_link) {
          qualExtension.extension.push({
            url: 'website',
            valueUri: qual.issuer_website_link
          });
        }

        if (qualExtension.extension.length > 0) {
          extensions.push(qualExtension);
        }
      });
    }

    if (extensions.length > 0) {
      org.extension = extensions;
    }

    // TELECOM
    const telecom: any[] = [];

    if (formData.phone_telecom?.value) {
      telecom.push({
        system: 'phone',
        value: formData.phone_telecom.value,
        use: formData.phone_telecom.use || 'work'
      });
    }

    if (formData.email_telecom?.value) {
      telecom.push({
        system: 'email',
        value: formData.email_telecom.value,
        use: formData.email_telecom.use || 'work'
      });
    }

    if (Array.isArray(formData.telecom)) {
      formData.telecom.forEach((t: any) => {
        if (t?.value) {
          telecom.push({
            system: t.system || 'other',
            value: t.value,
            use: t.use || 'work'
          });
        }
      });
    }

    if (telecom.length > 0) {
      org.telecom = telecom;
    }

    // ADDRESS
    if (Array.isArray(formData.address)) {
      org.address = formData.address
        .map((addr: any) => ({
          line: Array.isArray(addr.line) ? addr.line.filter((l: string) => l?.trim()) : undefined,
          city: addr.city || undefined,
          state: addr.state || undefined,
          postalCode: addr.postalCode || undefined,
          country: addr.country || undefined
        }))
        .filter((a: any) => Object.values(a).some(Boolean));
    }

    console.log('📋 FHIR Organization (partial update with protected extensions):', org);
    
    return org;
  }

  private mapOrgTypeToCode(type: string): string {
    switch (type.toLowerCase()) {
      case 'hospital': return 'prov';
      case 'clinic': return 'prov';
      case 'laboratory': return 'lab';
      case 'insurance': return 'ins';
      case 'education': return 'edu';
      default: return 'other';
    }
  }

  /**
   * Transform Organization FHIR resource to form data structure
   */
  private transformOrganizationToFormData(org: any): any {
    const formData: any = {
      name: org.name || '',
      type: org.type?.[0]?.text || org.type?.[0]?.coding?.[0]?.display || '',
    };

    // Extract extensions
    if (org.extension && Array.isArray(org.extension)) {
      const descExtension = org.extension.find((ext: any) => 
        ext.url === 'http://elikita.org/fhir/StructureDefinition/organization-description'
      );
      formData.description = descExtension?.valueString || '';

      const qualifications: any[] = [];
      org.extension.forEach((ext: any) => {
        if (ext.url?.includes('organization-qualification') && ext.extension) {
          const qual: any = {};
          
          ext.extension.forEach((nestedExt: any) => {
            if (nestedExt.url === 'description' && nestedExt.valueString) {
              qual.code = nestedExt.valueString;
            } else if (nestedExt.url === 'issuer' && nestedExt.valueString) {
              qual.issuer_name = nestedExt.valueString;
            } else if (nestedExt.url === 'website' && nestedExt.valueUri) {
              qual.issuer_website_link = nestedExt.valueUri;
            }
          });

          if (Object.keys(qual).length > 0) {
            qualifications.push(qual);
          }
        }
      });

      if (qualifications.length > 0) {
        formData.qualification = qualifications;
      }
    }

    // Extract telecom
    if (org.telecom && Array.isArray(org.telecom)) {
      let hasPhone = false;
      let hasEmail = false;
      const otherContacts: any[] = [];

      org.telecom.forEach((telecom: any) => {
        const system = (telecom.system || '').toLowerCase();
        
        if (system === 'phone' && !hasPhone) {
          formData.phone_telecom = {
            value: telecom.value || '',
            system: 'phone',
            use: telecom.use || 'work'
          };
          hasPhone = true;
        } else if (system === 'email' && !hasEmail) {
          formData.email_telecom = {
            value: telecom.value || '',
            system: 'email',
            use: telecom.use || 'work'
          };
          hasEmail = true;
        } else if (system !== 'phone' && system !== 'email') {
          otherContacts.push({
            value: telecom.value || '',
            system: telecom.system || '',
            use: telecom.use || 'work'
          });
        }
      });

      if (otherContacts.length > 0) {
        formData.telecom = otherContacts;
      }
    }

    // Extract addresses
    if (org.address && Array.isArray(org.address)) {
      formData.address = org.address.map((addr: any) => ({
        line: addr.line || [''],
        city: addr.city || '',
        state: addr.state || '',
        postalCode: addr.postalCode || '',
        country: addr.country || ''
      }));
    }

    console.log('📋 Transformed form data:', formData);
    return formData;
  }

  private getOrganizationFormFields() {
    return [
      {
        generalProperties: {
          fieldApiName: 'type',
          fieldName: "Organization's Category",
          fieldLabel: "Organization Category",
          fieldType: 'IndividualField',
          fieldPlaceholder: "Healthcare Provider, Insurance Company, Government",
          isArray: false,
          isGroup: false,
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      {
        generalProperties: {
          fieldApiName: "name",
          fieldName: "Name of Your Organization",
          fieldLabel: "Name of Your Organization",
          fieldType: "IndividualField",
          fieldPlaceholder: "Name of Your Organization",
          isArray: false,
          isGroup: false,
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      {
        generalProperties: {
          fieldApiName: "description",
          fieldName: "Description",
          fieldLabel: "Description of Your Organization",
          fieldPlaceholder: "Description of Your Organization",
          inputType: "textarea",
          fieldType: "IndividualField",
          isArray: false,
          isGroup: false,
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      {
        generalProperties: {
          fieldApiName: 'address',
          fieldName: 'Organization Address',
          fieldLabel: 'Address',
          fieldType: 'IndividualField',
          isArray: true,
          isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['line', 'city', 'state', 'postalCode', 'country'],
        groupFields: {
          line: {
            generalProperties: {
              fieldApiName: 'line',
              fieldName: 'Street Address',
              fieldLabel: 'Street Address',
              fieldType: 'IndividualField',
              isArray: true,
              isGroup: false
            }
          },
          city: {
            generalProperties: {
              fieldApiName: 'city',
              fieldName: 'City',
              fieldLabel: 'City',
              fieldType: 'IndividualField'
            }
          },
          state: {
            generalProperties: {
              fieldApiName: 'state',
              fieldName: 'State',
              fieldLabel: 'State',
              fieldType: 'IndividualField'
            }
          },
          postalCode: {
            generalProperties: {
              fieldApiName: 'postalCode',
              fieldName: 'Postal Code',
              fieldLabel: 'Postal Code',
              fieldType: 'IndividualField'
            }
          },
          country: {
            generalProperties: {
              fieldApiName: 'country',
              fieldName: 'Country',
              fieldLabel: 'Country',
              fieldType: 'IndividualField'
            }
          }
        }
      },
      {
        generalProperties: {
          fieldApiName: 'phone_telecom',
          fieldName: 'Phone Number',
          fieldLabel: 'Phone Number',
          fieldType: 'IndividualField',
          isArray: false,
          isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['value', 'use'],
        groupFields: {
          value: {
            generalProperties: {
              fieldApiName: 'value',
              fieldName: 'Phone Number',
              fieldLabel: 'Phone Number',
              fieldType: 'IndividualField'
            }
          },
          system: {
            generalProperties: {
              fieldApiName: 'system',
              fieldName: 'Contact Platform',
              fieldLabel: 'Contact Platform',
              fieldType: 'CodeField',
              value: 'phone',
              isHidden: true
            }
          },
          use: {
            generalProperties: {
              fieldApiName: 'use',
              fieldName: 'Contact Type',
              fieldLabel: 'Contact Type',
              fieldType: 'CodeField',
              value: 'work',
              isHidden: true
            }
          }
        }
      },
      {
        generalProperties: {
          fieldApiName: 'email_telecom',
          fieldName: 'Email',
          fieldLabel: 'Email',
          fieldType: 'IndividualField',
          isArray: false,
          isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['value', 'use'],
        groupFields: {
          value: {
            generalProperties: {
              fieldApiName: 'value',
              fieldName: 'Email Address',
              fieldLabel: 'Email Address',
              fieldType: 'IndividualField'
            }
          },
          system: {
            generalProperties: {
              fieldApiName: 'system',
              fieldName: 'Contact Platform',
              fieldLabel: 'Contact Platform',
              fieldType: 'CodeField',
              value: 'email',
              isHidden: true
            }
          },
          use: {
            generalProperties: {
              fieldApiName: 'use',
              fieldName: 'Contact Type',
              fieldLabel: 'Contact Type',
              fieldType: 'CodeField',
              value: 'work',
              isHidden: true
            }
          }
        }
      },
      {
        generalProperties: {
          fieldApiName: 'telecom',
          fieldName: 'Other Contacts',
          fieldLabel: 'Other Contacts',
          fieldType: 'IndividualField',
          isArray: true,
          isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['value', 'system', 'use'],
        groupFields: {
          value: {
            generalProperties: {
              fieldApiName: 'value',
              fieldName: 'Contact Value',
              fieldLabel: 'Contact',
              fieldType: 'IndividualField'
            }
          },
          system: {
            generalProperties: {
              fieldApiName: 'system',
              fieldName: 'Contact Platform',
              fieldLabel: 'Contact Platform',
              fieldType: 'CodeField'
            }
          },
          use: {
            generalProperties: {
              fieldApiName: 'use',
              fieldName: 'Contact Type',
              fieldLabel: 'Contact Type',
              fieldType: 'CodeField',
              value: 'work'
            }
          }
        }
      },
      {
        generalProperties: {
          fieldApiName: 'qualification',
          fieldName: "Qualifications",
          fieldLabel: "Organization's Qualifications",
          isArray: true,
          isGroup: true
        },
        keys: ['code', 'issuer_name', 'issuer_website_link'],
        groupFields: {
          code: {
            generalProperties: {
              fieldApiName: 'code',
              fieldName: 'Qualification Code',
              fieldLabel: 'Qualification Code',
              fieldType: 'IndividualField'
            }
          },
          issuer_name: {
            generalProperties: {
              fieldApiName: 'issuer_name',
              fieldName: 'Issuer Name',
              fieldLabel: 'Issuer Name',
              fieldType: 'IndividualField'
            }
          },
          issuer_website_link: {
            generalProperties: {
              fieldApiName: 'issuer_website_link',
              fieldName: 'Issuer Website Link',
              fieldLabel: 'Issuer Website Link',
              fieldType: 'IndividualField'
            }
          }
        }
      }
    ];
  }

  addOrganization() {
    forkJoin({
      sample: of('sample')
    }).subscribe((e: any) => {
      this.dialog.open(DynamicFormsV2Component, {
        maxHeight: '90vh',
        maxWidth: '900px',
        autoFocus: false,
        data: {
          formMetaData: {
            formName: "Hospital Record",
            formDescription: "Record / Edit Internal Hospital Information",
            submitText: "Submit",
            resourceType: 'Organization'
          },
          formFields: this.getOrganizationFormFields()
        }
      });
    });
  }

  addServices() {

    forkJoin({
      sample: of('sample')
    }).subscribe((e: any) => {
      this.dialog.open(DynamicFormsV2Component, {
        maxHeight: '90vh',
        maxWidth: '900px',
        autoFocus: false,
        data: {
          formMetaData: <formMetaData>{
            formName: "Service",
            formDescription: "Add a healthcare service that appointees can request for when booking",
            submitText: "Add Services",

          },
          formFields: <FormFields[]>[<IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'active',
              fieldName: "Active?",
              fieldType: 'IndividualField',
              inputType: 'toggle',
              isArray: false,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
          },


          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'service_category',
              fieldName: "Service Category",
              fieldType: 'IndividualField',
              fieldPlaceholder: "Paediatric, 	Community Health Care, Gynecology e.t.c. ",
              isArray: true,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },

            },



          },
          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'type',
              fieldName: "Subcategory / Area of Focus",
              fieldType: 'IndividualField',
              inputType: 'text',
              fieldPlaceholder: "	Paediatric Surgery, 	Immunization ",
              isArray: true,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
          },
          // Additional fields can be here - A codeable reference field  -  Location of the service. To have this form, we must
          //have another form for the Resource Type Location and the reference it from there
          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'name',
              fieldName: "Name",
              fieldType: 'IndividualField',
              inputType: 'text',
              isArray: false,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
          },

          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'otherDetails',
              fieldName: "Service Description",
              fieldType: 'IndividualField',
              inputType: 'textarea',
              isArray: false,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
          },
          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'photo',
              fieldName: "Service's Illustration",
              fieldType: 'IndividualField',
              inputType: 'photo_upload',
              isArray: false,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
          },
          <GroupField>{

            generalProperties: {
              fieldApiName: 'phone_telecom',
              fieldName: 'Phone Number',
              fieldLabel: 'Phone Number',
              fieldType: 'IndividualField',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },

              isArray: true,
              isGroup: true,
            },
            keys: ['value', 'use'],
            groupFields: {
              'value': <IndividualField>{
                generalProperties: {
                  fieldApiName: 'value',


                  fieldName: 'Phone Number ',
                  fieldLabel: 'Phone Number',

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  value: 'work',
                  isHidden: true,
                  isArray: false,
                  isGroup: false

                }


              },
              'system': <CodeField>{
                generalProperties: {
                  fieldApiName: 'system',
                  fieldName: 'Contact Platform',
                  fieldLabel: 'Contact Platform',
                  fieldType: 'CodeField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  value: "phone",
                  isHidden: true,
                  isArray: false,
                  isGroup: false
                },

              },
              'use': <CodeField>{
                generalProperties: {
                  fieldApiName: 'use',
                  fieldName: 'Contact Type',
                  fieldLabel: 'Contact Type',
                  fieldType: 'CodeField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false
                },

              },



            },
          },
          <GroupField>{

            generalProperties: {
              fieldApiName: 'email_telecom',
              fieldName: 'Email',
              fieldLabel: 'Email',
              fieldType: 'IndividualField',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },

              isArray: true,
              isGroup: true,
            },
            keys: ['value', 'use'],
            groupFields: {
              'value': <IndividualField>{
                generalProperties: {
                  fieldApiName: 'value',


                  fieldName: 'Email ',
                  fieldLabel: 'Email',

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false

                }


              },
              'system': <CodeField>{
                generalProperties: {
                  fieldApiName: 'system',
                  fieldName: 'Contact Platform',
                  fieldLabel: 'Contact Platform',
                  fieldType: 'CodeField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  value: "email",
                  isHidden: true,
                  isArray: false,
                  isGroup: false
                },

              },
              'use': <CodeField>{
                generalProperties: {
                  fieldApiName: 'use',
                  fieldName: 'Contact Type',
                  fieldLabel: 'Contact Type',
                  fieldType: 'CodeField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                  value: 'work',
                  isHidden: true,
                  isArray: false,
                  isGroup: false
                },

              },



            },
          },
          <GroupField>{

            generalProperties: {
              fieldApiName: 'telecom',
              fieldName: 'Other Contacts',
              fieldLabel: 'Other Contacts',
              fieldType: 'IndividualField',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },

              isArray: true,
              isGroup: true
            },
            keys: ['value', 'system', 'use'],
            groupFields: {
              'value': <IndividualField>{
                generalProperties: {
                  fieldApiName: 'value',


                  fieldName: 'Contact ',
                  fieldLabel: 'Contact',

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false

                }


              },
              'system': <CodeField>{
                generalProperties: {
                  fieldApiName: 'system',
                  fieldName: 'Contact Platform',
                  fieldLabel: 'Contact Platform',
                  fieldType: 'CodeField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false
                },

              },
              'use': <CodeField>{
                generalProperties: {
                  fieldApiName: 'use',
                  fieldName: 'Contact Type',
                  fieldLabel: 'Contact Type',
                  fieldType: 'CodeField',
                  value: 'work',
                  isHidden: true,
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false
                },

              },



            },
          },
          <IndividualField>{
            generalProperties: <generalFieldsData>{
              fieldApiName: 'appointmentRequired',
              fieldName: "Appointment Required?",
              fieldType: 'IndividualField',
              inputType: 'radio',
              isArray: false,
              isGroup: false,

              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
            },
            data: ['Yes', 'No']
          },
          <GroupField>{

            generalProperties: {
              fieldApiName: 'availableTime',
              fieldName: 'Available Time ',
              fieldLabel: 'Available Time for This Service',
              fieldType: 'IndividualField',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },

              isArray: true,
              isGroup: true
            },
            keys: ['daysOfWeek', 'availableStartTime', 'availableEndTime',],
            groupFields: {
              'daysOfWeek': <SingleCodeField>{
                generalProperties: {
                  fieldApiName: 'daysOfWeek',
                  fieldType: 'SingleCodeField',


                  fieldName: 'Days Of The Week ',
                  fieldLabel: 'Days of The Week',
                  inputType: 'checkbox',


                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false

                },

                data: ['Monday', 'Tue', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

              },
              'availableStartTime': <IndividualField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'availableStartTime',
                  fieldName: "Start Time ",
                  fieldType: 'IndividualField',
                  inputType: 'time',
                  isArray: false,
                  isGroup: false,

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
              },
              'availableEndTime': <CodeField>{
                generalProperties: {
                  fieldApiName: 'availableEndTime',
                  fieldName: ' End Time',
                  fieldLabel: 'End Time',
                  fieldType: 'IndividualField',
                  inputType: 'time',


                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: false,
                  isGroup: false
                },

              },



            },
          },


          ]
        }
      })
    })

  }

  addSlots() {
    forkJoin({
      sample: of('sample'),
      appointmentType: this.formFieldsDataService.getFormFieldSelectData('slot', 'appointmentType'),
      'serviceType': this.formFieldsDataService.getFormFieldSelectData('slot', 'serviceType'),
    }).subscribe({
      next: (g: any) => {
        this.dialog.open(DynamicFormsV2Component, {
          maxHeight: '90vh',
          maxWidth: '900px',
          autoFocus: false,
          data: {
            formMetaData: <formMetaData>{
              formName: "Appointment Slots",
              formDescription: "Add bookable slots for one or more registered healthcare services",
              submitText: "Add Slots",


            },
            formFields: <FormFields[]>[
              <GroupField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'serviceTypeGroup',
                  fieldName: "Type of Service",
                  fieldType: 'IndividualField',
                  inputType: 'text',
                  isArray: true,
                  isGroup: true,

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
                keys: ['serviceType', 'serviceType2'],
                groupFields: {
                  'serviceType': <IndividualReferenceField>{
                    generalProperties: <generalFieldsData>{
                      fieldApiName: 'serviceType',
                      fieldName: "Type of Service",
                      fieldType: 'IndividualReferenceField',
                      inputType: 'text',
                      isArray: false,
                      isGroup: false,
                      controllingField: [{
                        isAControlField: true,
                        dependentFieldVisibilityTriggerValue: 'Others',
                        controlledFieldDependencyId: 'serviceTypeGroup.serviceType2'

                      }],

                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },
                    },
                    data: g.serviceType
                  },

                  'serviceType2': <IndividualField>{
                    generalProperties: <generalFieldsData>{
                      fieldApiName: 'serviceType2',
                      fieldLabel: "Enter a type of healthcare service",
                      fieldName: "Other Type of Service",
                      fieldType: 'IndividualField',
                      inputType: 'text',
                      isArray: false,
                      isGroup: false,
                      dependence_id: "serviceTypeGroup.serviceType2",
                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },
                    },
                  },


                }
              },
              // a codeableconcept field of "specialty can be placed here "
              // - obtain values from a valueset url (a Preferred binding)

              <GroupField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'appointmentTypeGroup',
                  fieldName: "Type of Appointment",
                  fieldType: 'IndividualField',
                  inputType: 'text',
                  isArray: false,
                  isGroup: true,

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
                keys: ['appointmentType', 'appointmentType2'],
                groupFields: {
                  'appointmentType': <CodeableConceptField>{
                    generalProperties: <generalFieldsData>{
                      fieldApiName: 'appointmentType',
                      fieldName: "Type of Appointment",
                      fieldType: 'CodeableConceptField',
                      inputType: 'text',
                      isArray: false,
                      isGroup: false,
                      controllingField: [{
                        isAControlField: true,
                        dependentFieldVisibilityTriggerValue: 'Others',
                        controlledFieldDependencyId: 'appointmentTypeGroup.appointmentType2'

                      }],
                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },
                    },
                    data: g.appointmentType
                  },
                  'appointmentType2': <IndividualField>{
                    generalProperties: <generalFieldsData>{
                      fieldApiName: 'appointmentType2',
                      fieldName: "Other type of Appointment",
                      fieldLabel: "Enter a type of Appointment",
                      fieldType: 'CodeableConceptField',
                      inputType: 'text',
                      isArray: false,
                      isGroup: false,
                      dependence_id: 'appointmentTypeGroup.appointmentType2',
                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },
                    },

                    data: [
                      `OutPatient Appointment$#$OutPatient$#$https://sampleOrgCode`,
                      `Community Health Program Appointment$#$Community Health$#$https://sampleOrgCode`,
                    ]
                  },

                }
              },

              //slot status will auomatically change when
              // users starts boooking, so, this feld will be hidden
              <SingleCodeField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'status',
                  fieldName: "Status",
                  fieldType: 'SingleCodeField',
                  isHidden: true,
                  isArray: false,
                  isGroup: false,
                  value: 'free',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
                data: ['busy', 'free', 'busy-unavailable', 'busy-tentative', 'entered-in-error']
              },
              <IndividualField>{
                generalProperties: <generalFieldsData>{
                  fieldApiName: 'canBeOveBooked',
                  fieldName: "Can Slot Be Overbooked",
                  fieldType: 'IndividualField',
                  inputType: 'toggle',
                  isArray: false,
                  isGroup: false,

                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },
                },
              },
              <GroupField>{

                generalProperties: {
                  fieldApiName: 'availableTime',
                  fieldName: 'Available Time ',
                  fieldLabel: 'Available Time for This Service',
                  fieldType: 'IndividualField',
                  auth: {
                    read: 'all',
                    write: 'doctor, nurse'
                  },

                  isArray: true,
                  isGroup: true
                },
                keys: ['daysOfWeek', 'availableStartTime', 'availableEndTime',],
                groupFields: {
                  'daysOfWeek': <SingleCodeField>{
                    generalProperties: {
                      fieldApiName: 'daysOfWeek',

                      fieldType: 'SingleCodeField',
                      fieldName: 'Days Of The Week ',
                      fieldLabel: 'Days of The Week',
                      inputType: 'checkbox',


                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },

                      isArray: false,
                      isGroup: false

                    },

                    data: ['Monday', 'Tue', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

                  },
                  'availableStartTime': <IndividualField>{
                    generalProperties: <generalFieldsData>{
                      fieldApiName: 'availableStartTime',
                      fieldName: "Start Time ",
                      fieldLabel: "Start Time",
                      fieldType: 'IndividualField',
                      inputType: 'time',
                      isArray: false,
                      isGroup: false,

                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },
                    },
                  },
                  'availableEndTime': <CodeField>{
                    generalProperties: {
                      fieldApiName: 'availableEndTime',
                      fieldName: ' End Time',
                      fieldLabel: 'End Time',
                      fieldType: 'IndividualField',
                      inputType: 'time',


                      auth: {
                        read: 'all',
                        write: 'doctor, nurse'
                      },

                      isArray: false,
                      isGroup: false
                    },

                  },



                },
              },
            ]
          }
        })
      },
      error: (err) => {
        this.errorService.openandCloseError("An error ocurred while preparing forms for slots")
      }



    })

  }
}
